// Display formatting. Times show in Asia/Muscat (the operating timezone) with the
// zone named, because a permit window must never be misread across devices.
const TZ = 'Asia/Muscat'

const dateTimeFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ,
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

const timeFmt = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: false })
const dateFmt = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, day: '2-digit', month: 'short', year: 'numeric' })

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '-'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? String(iso) : `${dateTimeFmt.format(d)} GST`
}

export function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '-'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? String(iso) : timeFmt.format(d)
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '-'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? String(iso) : dateFmt.format(d)
}

export function shortDigest(digest: string | null | undefined): string {
  return digest ? `${digest.slice(0, 8)}...${digest.slice(-4)}` : '-'
}

/** Value for <input type="datetime-local"> in Muscat time. */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const muscat = new Date(d.getTime() + 4 * 3600_000)
  return muscat.toISOString().slice(0, 16)
}

/** Parses a datetime-local value entered in Muscat time to an ISO string with offset. */
export function fromLocalInput(value: string): string | null {
  if (!value) return null
  return `${value}:00+04:00`
}

/**
 * The ISO argument for a datetime-local input, which shows whole minutes. While the input
 * still shows the minute of `exactIso` (a default or "now" that the app filled in), that
 * exact time is sent: the start of the minute can be up to 59 s earlier than meant, the
 * next minute up to 59 s later, and the server compares to the second (a visit "before"
 * the submission, a validation that starts after the person tries to accept). A time the
 * person typed is sent as typed.
 */
export function inputTimeArg(input: string, exactIso: string | null | undefined): string | null {
  if (!input) return null
  if (exactIso && toLocalInput(exactIso) === input) return exactIso
  return fromLocalInput(input)
}

// -----------------------------------------------------------------------------
// Day-month-year entry (src/components/DateTimeInput.tsx). The canonical values stay
// the ones above ('YYYY-MM-DDTHH:mm' in Muscat time, 'YYYY-MM-DD' for dates), so
// inputTimeArg and every caller keep working; only what people type and see is
// DD/MM/YYYY and 24-hour HH:mm, whatever the browser's locale.
// -----------------------------------------------------------------------------
const pad = (n: number): string => String(n).padStart(2, '0')

/** 'YYYY-MM-DD' for a typed day/month/year (separators / - . or space, or 8 digits), else null. */
export function parseDmyDate(text: string): string | null {
  const t = text.trim()
  const m = /^(\d{1,2})[/.\-\s]+(\d{1,2})[/.\-\s]+(\d{4})$/.exec(t) ?? /^(\d{2})(\d{2})(\d{4})$/.exec(t)
  if (!m) return null
  const day = Number(m[1])
  const month = Number(m[2])
  const year = Number(m[3])
  if (year < 1900 || year > 2999 || month < 1 || month > 12 || day < 1) return null
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate()
  if (day > daysInMonth) return null
  return `${year}-${pad(month)}-${pad(day)}`
}

/** 'HH:mm' for a typed 24-hour time ('7:30', '07:30', '0730', '07.30'), else null. */
export function parse24hTime(text: string): string | null {
  const t = text.trim()
  const m = /^(\d{1,2})[:.](\d{2})$/.exec(t) ?? /^(\d{2})(\d{2})$/.exec(t)
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  if (h > 23 || min > 59) return null
  return `${pad(h)}:${pad(min)}`
}

/** 'DD/MM/YYYY' for a 'YYYY-MM-DD' (or 'YYYY-MM-DDTHH:mm') value; '' when there is none. */
export function toDmyText(value: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '')
  return m ? `${m[3]}/${m[2]}/${m[1]}` : ''
}

/** 'HH:mm' of a 'YYYY-MM-DDTHH:mm' value; '' when there is none. */
export function toTimeText(value: string | null | undefined): string {
  const m = /T(\d{2}:\d{2})/.exec(value ?? '')
  return m ? m[1] : ''
}

export type DmyEntry = { value: string; error: string | null }

export const DMY_DATE_ERROR = 'Enter the date as day/month/year, for example 05/11/2026.'
export const TIME_24H_ERROR = 'Enter the time as 24-hour HH:MM, for example 07:30 or 19:45.'

/**
 * Joins a typed date and time into the canonical 'YYYY-MM-DDTHH:mm' value. Both empty is
 * no value and no error; anything else needs both parts valid.
 */
export function joinDmyDateTime(dateText: string, timeText: string): DmyEntry {
  const d = dateText.trim()
  const t = timeText.trim()
  if (!d && !t) return { value: '', error: null }
  const date = parseDmyDate(d)
  const time = parse24hTime(t)
  if (!d) return { value: '', error: 'Enter the date as well as the time (day/month/year).' }
  if (!date) return { value: '', error: DMY_DATE_ERROR }
  if (!t) return { value: '', error: 'Enter the time as well (24-hour HH:MM).' }
  if (!time) return { value: '', error: TIME_24H_ERROR }
  return { value: `${date}T${time}`, error: null }
}

/** The canonical 'YYYY-MM-DD' value for a typed date; empty is no value and no error. */
export function dmyDateEntry(dateText: string): DmyEntry {
  const d = dateText.trim()
  if (!d) return { value: '', error: null }
  const date = parseDmyDate(d)
  return date ? { value: date, error: null } : { value: '', error: DMY_DATE_ERROR }
}

/** "4 hours", "1 hour", "1.5 hours". */
export function hoursText(hours: number): string {
  const n = Math.round(hours * 100) / 100
  return `${n} hour${n === 1 ? '' : 's'}`
}

/** A gas retest interval in hours: the hours entered (0015) or, for older permits, minutes / 60. */
export function retestHours(source: { retest_interval_hours?: unknown; retest_interval_minutes?: unknown } | null | undefined): number | null {
  if (!source) return null
  if (typeof source.retest_interval_hours === 'number' && Number.isFinite(source.retest_interval_hours)) return source.retest_interval_hours
  if (typeof source.retest_interval_minutes === 'number' && Number.isFinite(source.retest_interval_minutes)) {
    return Math.round((source.retest_interval_minutes / 60) * 100) / 100
  }
  return null
}

export function relativeTo(iso: string | null | undefined, nowIso?: string): string {
  if (!iso) return ''
  const now = nowIso ? new Date(nowIso).getTime() : Date.now()
  const diff = new Date(iso).getTime() - now
  const mins = Math.round(Math.abs(diff) / 60_000)
  const span = mins < 60 ? `${mins} min` : mins < 48 * 60 ? `${Math.round(mins / 60)} h` : `${Math.round(mins / 1440)} days`
  return diff >= 0 ? `in ${span}` : `${span} ago`
}

export function labelFor(options: readonly { code: string; label: string }[], code: string | null | undefined): string {
  if (!code) return '-'
  return options.find((o) => o.code === code)?.label ?? code
}
